import { useCallback, useState } from 'react';

import { FilePlus, WandSparkles } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { toast as sonnerToast } from 'sonner';

import { NewChip } from '../../../../components/atoms/NewChip';
import { Button } from '@/components/atoms/new-button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/Popover';

interface GenerateDetailsButtonProps {
  isGeneratingDetails: boolean;
  onGenerateDetails: (file: File) => void;
}

export const GenerateDetailsButton = ({ isGeneratingDetails, onGenerateDetails }: GenerateDetailsButtonProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        // this should be handled by OS file viewer but checking just in case
        if (file.type === 'application/pdf') {
          onGenerateDetails(file);
        } else {
          sonnerToast.info('Only PDF files can be processed for automatic detail extraction');
        }
      }
    },
    [onGenerateDetails],
  );

  const maxFiles = 1;
  const maxSize = 20 * 1024 * 1024; // 20MB

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    onDropAccepted: onDrop,
    maxSize,
    maxFiles,
    accept: {
      'application/pdf': ['.pdf'],
    },
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={'outline'}
          className="hover:border-brand-secondary relative h-auto w-fit bg-gray-50 p-1.5 px-2.5 hover:bg-gray-50"
        >
          <WandSparkles className="text-brand-secondary" />
          Generate details from document
          <NewChip />
        </Button>
      </PopoverTrigger>
      <PopoverContent side="bottom" sideOffset={12} className="w-full max-w-sm">
        <div className="space-y-2">
          <h3 className="text-sm font-medium">Upload Document</h3>
          <p className="text-xs text-gray-600">
            Upload a structured PDF document like a {t('claim form')} to automatically generate case details. You'll
            still be able to review and edit the details manually.
          </p>

          <div
            {...getRootProps()}
            className={`relative mt-3 flex flex-col items-center justify-center rounded-lg border-2 border-dashed bg-slate-50 p-4 py-8 ${
              isGeneratingDetails ? 'cursor-wait opacity-60' : 'cursor-pointer'
            }`}
          >
            <input {...getInputProps()} disabled={isGeneratingDetails} />

            <Button disabled={isGeneratingDetails} variant={'brand'} className="h-auto px-3 font-semibold">
              <FilePlus />
              Upload file
            </Button>
            <p className="mt-3 text-center text-xs leading-4 text-gray-600">
              {isGeneratingDetails ? 'Extracting case details...' : 'Or drag here'}
            </p>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
